import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  sendUserActivityHeartbeat,
  USER_ACTIVITY_PAGE_EVENT,
  type UserActivityPage
} from '../services/userActivity';

const HEARTBEAT_INTERVAL_MS = 60_000;
const ACTIVE_INPUT_WINDOW_MS = 120_000;
const CLIENT_INSTANCE_KEY = 'bonzaiq.userActivity.clientInstance';

function clientInstanceId(): string {
  const existing = window.sessionStorage.getItem(CLIENT_INSTANCE_KEY);
  if (existing) return existing;
  const created = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(CLIENT_INSTANCE_KEY, created);
  return created;
}

function routePage(pathname: string): UserActivityPage | null {
  if (pathname === '/tech' || pathname === '/customer') return null;
  if (/^\/tsfad\/[^/]+\/remote$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'tsfad-remote' };
  if (/^\/tsfad\/[^/]+$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'tsfad' };
  if (/^\/repair\/[^/]+$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'repair' };
  if (/^\/install\/[^/]+\/pre$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'install-prep' };
  if (/^\/install\/[^/]+\/exec$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'install-execution' };
  if (/^\/install\/[^/]+\/verify$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'install-verification' };
  if (/^\/install\/[^/]+\/decom$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'install-decommission' };
  if (/^\/maintenance\/[^/]+\/checkup$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'maintenance-checkup' };
  if (/^\/maintenance\/[^/]+\/preventative$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'maintenance-preventative' };
  if (/^\/safety-upgrade\/[^/]+\/implementation$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'safety-upgrade-implementation' };
  if (/^\/safety-upgrade\/[^/]+$/.test(pathname)) return { surface: 'TASK_WORKSPACE', pageKey: 'safety-upgrade' };
  return null;
}

const UserActivityTracker: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const pageRef = useRef<UserActivityPage | null>(routePage(location.pathname));
  const lastInteractionAtRef = useRef(Date.now());
  const lastHeartbeatAtRef = useRef(Date.now());
  const requestQueueRef = useRef<Promise<void>>(Promise.resolve());
  const clientInstanceIdRef = useRef<string | null>(null);

  const queueHeartbeat = useCallback((page: UserActivityPage | null, activeSeconds: number) => {
    if (!token || !page) return;
    if (!clientInstanceIdRef.current) clientInstanceIdRef.current = clientInstanceId();
    const payload = {
      clientInstanceId: clientInstanceIdRef.current,
      ...page,
      activeSeconds,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null
    };
    requestQueueRef.current = requestQueueRef.current
      .catch(() => undefined)
      .then(() => sendUserActivityHeartbeat(payload))
      .catch(error => console.warn('[user-activity] heartbeat failed', error instanceof Error ? error.message : error));
  }, [token]);

  const flushCurrentPage = useCallback(() => {
    const now = Date.now();
    const elapsedSeconds = Math.min(90, Math.max(0, Math.round((now - lastHeartbeatAtRef.current) / 1000)));
    const isActive = document.visibilityState === 'visible' && now - lastInteractionAtRef.current <= ACTIVE_INPUT_WINDOW_MS;
    lastHeartbeatAtRef.current = now;
    queueHeartbeat(pageRef.current, isActive ? elapsedSeconds : 0);
  }, [queueHeartbeat]);

  useEffect(() => {
    const nextPage = routePage(location.pathname);
    flushCurrentPage();
    pageRef.current = nextPage;
    lastHeartbeatAtRef.current = Date.now();
    if (nextPage) queueHeartbeat(nextPage, 0);
  }, [flushCurrentPage, location.pathname, queueHeartbeat]);

  useEffect(() => {
    if (!token) return;
    const onPage = (event: Event) => {
      const nextPage = (event as CustomEvent<UserActivityPage>).detail;
      if (!nextPage?.surface || !nextPage?.pageKey) return;
      if (pageRef.current?.surface === nextPage.surface && pageRef.current?.pageKey === nextPage.pageKey) return;
      flushCurrentPage();
      pageRef.current = nextPage;
      lastHeartbeatAtRef.current = Date.now();
      queueHeartbeat(nextPage, 0);
    };
    const onInteraction = () => { lastInteractionAtRef.current = Date.now(); };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushCurrentPage();
      else lastInteractionAtRef.current = Date.now();
    };
    window.addEventListener(USER_ACTIVITY_PAGE_EVENT, onPage as EventListener);
    window.addEventListener('pointerdown', onInteraction, { passive: true });
    window.addEventListener('keydown', onInteraction);
    window.addEventListener('scroll', onInteraction, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(flushCurrentPage, HEARTBEAT_INTERVAL_MS);
    queueHeartbeat(pageRef.current, 0);
    return () => {
      window.clearInterval(timer);
      flushCurrentPage();
      window.removeEventListener(USER_ACTIVITY_PAGE_EVENT, onPage as EventListener);
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
      window.removeEventListener('scroll', onInteraction, true);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushCurrentPage, queueHeartbeat, token]);

  return null;
};

export default UserActivityTracker;
